import React, { useEffect, useState } from 'react';

import { Box } from '@chakra-ui/react';
import { closeCompetencySidePanelAction } from 'actions/competencySidePanelActions';
import ClickToScrollNavBar from 'components/ClickToScrollNavBar';
import SkeletonLoader from 'components/SkeletonLoader';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import Feedbacks from 'screens/StrengthDetail/Feedbacks';
import ScoreDetail from 'screens/StrengthDetail/ScoreDetail';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';
import styled from 'styled-components';
import { StyledDrawer } from 'styles/admin';
import { Divider1, SpaceBetweenContainer, VerticallyCenterContainer } from 'styles/common';

import { FEEDBACK_STRENGTH_TYPES, MISCELLANEOUS_COMPETENCY_ID, SENTIMENTS_TYPES } from 'constants/ProjectConstants';
import { capitalize } from 'constants/helper';
import { CloseIcon2, FeedbackSvgIcon, PraiseSvgIcon } from 'constants/icons';
import theme from 'constants/theme';

import {
  getAllFeedback,
  getAllStats,
  getCompetencyStats,
  getFeedbackV1,
  getMiscellaneousFeedback,
} from 'services/developments';
import { getEmployeeDetails } from 'services/employee';

import { Typography12, Typography13, Typography14 } from 'uiToolkit/Typography';

import NoFeedbacksMessage from '../screens/performanceReview/components/NoFeedbacksMessage';

const MISC_DESCRIPTION = 'Praises/Advices not linked to a competency';

const ItemsArray = [
  {
    id: 'appreciative',
    header: ({ count, isSelected }) => (
      <VerticallyCenterContainer>
        <PraiseSvgIcon containerSize="fixed12px" size="fixed12px" />
        <Typography13
          color={isSelected ? theme.palette.primary.light : 'textSecondary'}
          weight={isSelected ? 600 : 100}
          leftGutter={1}
        >
          {/* {FEEDBACK_STRENGTH_TYPES.APPRECIATIVE} */}
          {count ? `${FEEDBACK_STRENGTH_TYPES.APPRECIATIVE} (${count})` : FEEDBACK_STRENGTH_TYPES.APPRECIATIVE}
        </Typography13>
      </VerticallyCenterContainer>
    ),
    component: ({
      employeeId,
      tagId,
      strength,
      employeeDisplayName,
      isLoggedInEmployeePanel,
      hideShareFeedbackButtons = false,
    }) => {
      let fetchData = (limit, offset) => getAllFeedback({ employeeId, limit, offset, feedbackType: 'KUDOS' });

      if (tagId) {
        if (tagId === MISCELLANEOUS_COMPETENCY_ID) {
          fetchData = (limit, offset) =>
            getMiscellaneousFeedback({ employeeId, limit, offset, feedbackType: 'KUDOS', tagId });
        } else {
          fetchData = (limit, offset) => getFeedbackV1({ employeeId, limit, offset, feedbackType: 'KUDOS', tagId });
        }
      }

      let headerText = 'Praise(s) received';

      if (employeeDisplayName) headerText = `Praise(s) ${employeeDisplayName} has received`;
      if (isLoggedInEmployeePanel) headerText = 'Praise(s) you have received';

      return (
        <Feedbacks
          headerText={headerText}
          type={SENTIMENTS_TYPES.appreciative.value}
          hasCount={false}
          count={strength ? strength?.stats?.appreciativeFeedbackCount : 0}
          noHorizontalPadding
          employeeDisplayName={employeeDisplayName}
          fetchDataFn={fetchData}
          employeeId={employeeId}
          hideShareFeedbackButtons={hideShareFeedbackButtons}
        />
      );
    },
  },
  {
    id: 'constructive',
    header: ({ count, isSelected }) => (
      <VerticallyCenterContainer>
        <FeedbackSvgIcon containerSize="fixed12px" size="fixed12px" />
        <Typography13
          color={isSelected ? theme.palette.primary.light : 'textSecondary'}
          weight={isSelected ? 600 : 100}
          leftGutter={1}
        >
          {count ? `${FEEDBACK_STRENGTH_TYPES.CONSTRUCTIVE} (${count})` : FEEDBACK_STRENGTH_TYPES.CONSTRUCTIVE}
        </Typography13>
      </VerticallyCenterContainer>
    ),
    component: ({
      employeeId,
      tagId,
      strength,
      employeeDisplayName,
      isLoggedInEmployeePanel,
      hideShareFeedbackButtons = false,
    }) => {
      let fetchData = (limit, offset) => getAllFeedback({ employeeId, limit, offset, feedbackType: 'FEEDBACK' });

      if (tagId) {
        if (tagId === MISCELLANEOUS_COMPETENCY_ID) {
          fetchData = (limit, offset) =>
            getMiscellaneousFeedback({
              employeeId,
              limit,
              offset,
              feedbackType: 'FEEDBACK',
              tagId,
            });
        } else {
          fetchData = (limit, offset) => getFeedbackV1({ employeeId, limit, offset, feedbackType: 'FEEDBACK', tagId });
        }
      }

      let headerText = 'Advice received';

      if (employeeDisplayName) headerText = `Advice ${employeeDisplayName} has received`;
      if (isLoggedInEmployeePanel) headerText = 'Advice you have received';

      return (
        <Feedbacks
          headerText={headerText}
          type={SENTIMENTS_TYPES.constructive.value}
          hasCount={false}
          count={strength ? strength?.stats?.constructiveFeedbackCount : 0}
          noHorizontalPadding
          employeeDisplayName={employeeDisplayName}
          fetchDataFn={fetchData}
          employeeId={employeeId}
          hideShareFeedbackButtons={hideShareFeedbackButtons}
        />
      );
    },
  },
];

const CompetancySidePanel = () => {
  const loggedInEmployeeId = useSelector(selectLoggedInEmployeeId, shallowEqual);

  const {
    isOpen,
    tagTitle,
    tagDescription: tagDescriptionFromSelector = '',
    tagId,
    employeeId,
    onClose,
    hideShareFeedbackButtons = false,
  } = useSelector((state) => state.competencySidePanelDetails);

  const dispatch = useDispatch();

  const [strength, setStrength] = useState(null);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setStrength(null);
    setEmployeeDetails(null);
    setLoading(false);
    setTimeout(() => {
      onClose();
      dispatch(closeCompetencySidePanelAction());
    }, 0);
  };

  const handleKeyDown = ({ keyCode }) => {
    // close on esc key
    if (keyCode === 27) handleClose();
  };

  const getData = () => {
    if (employeeId) {
      setStrength(null);
      setEmployeeDetails(null);
      setLoading(true);

      if (tagId) {
        Promise.all([
          getCompetencyStats({
            employeeId,
            competencyId: tagId === MISCELLANEOUS_COMPETENCY_ID ? 'miscellaneous' : tagId,
          }).then((strength) => {
            setStrength(strength);
            return strength;
          }),
          getEmployeeDetails(employeeId).then((emp) => {
            setEmployeeDetails(emp);
            return emp;
          }),
        ])
          .catch(() => {})
          .finally(() => setLoading(false));
      } else {
        getAllStats({
          employeeId,
        })
          .then((strength) => {
            setStrength(strength);
            return strength;
          })
          .catch(() => {})
          .finally(() => setLoading(false));
      }
    }
  };

  useEffect(() => {
    if (employeeId) getData();
  }, [tagId, employeeId]);

  const tagDescription = tagDescriptionFromSelector || strength?.description;

  return (
    <StyledDrawer
      anchor="right"
      open={isOpen}
      ModalProps={{ onBackdropClick: handleClose, disableEnforceFocus: true }}
      onKeyDown={handleKeyDown}
    >
      <Box display="flex" flexDirection="column" height="100%">
        <SpaceBetweenContainer topGutter={1} bottomGutter={1.5} style={{ alignItems: 'center' }}>
          <Typography14 style={{ overflowWrap: 'break-word', width: '95%' }} weight={600}>
            {tagTitle}
          </Typography14>
          <CloseIcon2
            size="fixed12px"
            color={theme.palette.icons.blackv1}
            onClick={handleClose}
            style={{ marginRight: '-8px' }}
          />
        </SpaceBetweenContainer>

        {tagDescription === MISC_DESCRIPTION ? (
          <MiscText color={theme.palette.text.heading}>{tagDescription}</MiscText>
        ) : (
          <>
            {tagDescription ? (
              <DescriptionText color={theme.palette.text.label}>{tagDescription}</DescriptionText>
            ) : null}
          </>
        )}

        {tagId ? (
          <>
            <Divider1 topGutter={3} bottomGutter={3} />
            {loading ? (
              <SkeletonLoader skeletons={['100%']} />
            ) : strength ? (
              <>
                {tagId === MISCELLANEOUS_COMPETENCY_ID ? null : (
                  <ScoreDetailContainer>
                    <ScoreDetail
                      strengthName={strength.name}
                      score={strength.stats.score}
                      benchmarkValue={strength.stats.benchmarkScore}
                      level={strength.stats.level}
                      status={capitalize(strength.strengthLevel)}
                      scoreBarHeight="8px"
                    />
                  </ScoreDetailContainer>
                )}
              </>
            ) : (
              <NoFeedbacksMessage employeeId={employeeId} />
            )}
          </>
        ) : null}

        {(tagId === null || strength) && employeeId && (
          <StyledClickToScrollNavBar
            sectionList={ItemsArray.map(({ component, header, id }) => ({
              id,
              component: () =>
                component({
                  employeeId,
                  tagId,
                  strength,
                  employeeDisplayName: employeeDetails?.displayName || '',
                  isLoggedInEmployeePanel: loggedInEmployeeId === employeeId,
                  hideShareFeedbackButtons,
                }),
              header: (isSelected) =>
                header({
                  isSelected,
                  // following is this component specific
                  count:
                    id === 'appreciative'
                      ? strength?.stats?.appreciativeFeedbackCount || 0
                      : strength?.stats?.constructiveFeedbackCount || 0,
                }),
            }))}
          />
        )}
      </Box>
    </StyledDrawer>
  );
};

const ScoreDetailContainer = styled.div`
  margin-bottom: 1rem;
  margin-top: -5px;
  .MuiPaper-root {
    box-shadow: none;
    padding: 0;
  }

  .MuiTypography-body1 {
    font-size: 0.85rem;
  }
  svg {
    width: 30px;
    height: 30px;
  }
`;

const StyledClickToScrollNavBar = styled(ClickToScrollNavBar)`
  > div:first-child {
    right: 0;
  }
  > div:nth-child(2) {
    padding-right: 0;
  }
`;

const DescriptionText = styled(Typography12)`
  height: 100px;
  white-space: pre-line;
  overflow-y: scroll;
`;

const MiscText = styled(Typography14)`
  height: 100px;
  white-space: pre-line;
  overflow-y: scroll;
`;

export default CompetancySidePanel;
